import { EBool } from '~/src/dash/api/types/bool';
import { generateChannelsOptionsList } from '~/src/dash/apiV2/channels';
import { generateChannelGroupsOptionsList } from '~/src/dash/apiV2/channels';
import { ISimpleDeal } from '~/src/dash/apiV2/deals';
import { generateSalespersonOptionsList } from '~/src/dash/apiV2/deals/filters';
import { generateDemandSourcesOptionsList } from '~/src/dash/apiV2/demandSources';
import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { generateOptionsListAsPromise } from '~/src/dash/components/dropdowns/common';
import {
  convertSetIntoArrayOfNumbers,
  convertSetIntoArrayOfStrings,
  convertSetIntoSingleNumber,
} from '~/src/dash/components/filters/conversions';
import { IExtendedIFilterRowObject } from '~/src/dash/components/filters/FilterRowV2';
import { DealTypeOptions } from '~/src/dash/components/filters/options/deals';
import { EStatus, StatusOptions } from '~/src/dash/components/filters/status';
import {
  IListViewTableColumns,
  IListViewTablePagination,
  IListViewTableRow,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  DateTimeRenderUnchanged,
  EllipsisWithTooltipRender,
  MultiCurrencyRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { TierOptions } from '~/src/dash/utils/tiers';
import {
  DealChannelGroupsListModalLink,
  DealChannelsListModalLink,
  DealPartnerRender,
  DealTierRender,
  DealTypeRender,
} from '~/src/dash/views/deal/dealsV2/DealsListView/common';

export interface IDealsListViewConfig {
  cypressClassName: string;
  startToEndDateRanges: IStartToEndDateRanges;
  timezone: string;
  filters: Record<string, IExtendedIFilterRowObject>;
  columns: IListViewTableColumns;
  table: IListViewTablePagination;
  activeColumnKeys: string[];
  columnSelector: {
    resetKeys: string[];
  };
}

export const dealsListViewConfig: IDealsListViewConfig = {
  cypressClassName: 'dealslistview',
  startToEndDateRanges: {
    startDateFrom: null,
    startDateTo: null,
    endDateFrom: null,
    endDateTo: null,
  },
  timezone: '',
  filters: {
    Status: {
      key: 'Status',
      tKey: 'Status',
      getList: () => generateOptionsListAsPromise(StatusOptions),
      selectedLimit: 1,
      isSelectionRequired: true,
      selectedOptionIDs: new Set([EStatus.Active]),
      disableSearch: true,
      disableSort: true,
      displayLabelWithSelection: true,
      selectedOptionIDsOnReset: new Set([EStatus.ShowAll]),
      queryKey: 'active',
      formatQueryValue: convertSetIntoSingleNumber,
    },
    Channels: {
      key: 'Channels',
      tKey: 'Channels',
      getList: generateChannelsOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'channel_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    ChannelGroups: {
      key: 'ChannelGroups',
      tKey: 'ChannelGroups',
      getList: generateChannelGroupsOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'channel_group_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    DealType: {
      key: 'DealType',
      tKey: 'DealType',
      getList: () => generateOptionsListAsPromise(DealTypeOptions),
      selectedLimit: 0,
      disableSearch: true,
      disableSort: true,
      queryKey: 'deal_types',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    DealTier: {
      key: 'DealTier',
      tKey: 'DealTier',
      getList: () => generateOptionsListAsPromise(TierOptions),
      selectedLimit: 0,
      disableSearch: true,
      disableSort: true,
      queryKey: 'deal_tiers',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
    Partner: {
      key: 'Partner',
      tKey: 'Partner',
      getList: generateDemandSourcesOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      queryKey: 'partners',
      formatQueryValue: convertSetIntoArrayOfStrings,
    },
    Salesperson: {
      key: 'Salesperson',
      tKey: 'Salesperson',
      getList: generateSalespersonOptionsList,
      selectedLimit: 0,
      extractStringToSort: (option) => option.label,
      displayID: true,
      enableLabelTooltip: true,
      queryKey: 'salesperson_ids',
      formatQueryValue: convertSetIntoArrayOfNumbers,
    },
  },
  table: {
    page: 0,
    rowsPerPage: 10,
    order: 'asc',
    orderBy: 'start_date',
  },
  activeColumnKeys: [
    'DealName',
    'ExternalDealID',
    'Partner',
    'DealType',
    'Channels',
    'ChannelGroups',
    'Revenue',
    'StartDate',
    'EndDate',
  ],
  columnSelector: {
    resetKeys: [
      'DealName',
      'ExternalDealID',
      'Partner',
      'DealType',
      'Channels',
      'ChannelGroups',
      'Revenue',
      'StartDate',
      'EndDate',
    ],
  },
  columns: {
    ID: {
      formatValue: (value: number) => value.toString(),
      isHidden: true,
      isSearchable: true,
      key: 'ID',
      label: 'ID',
      requestKey: 'ID',
      responseKey: 'ID',
      valueType: 'string',
    },
    Status: {
      defaultOrderDirection: 'desc',
      formatValue: (value: number) => value === (EBool.EBool_TRUE as number),
      headerRenderType: 'switch',
      key: 'Status',
      tKey: 'Active',
      label: 'Active',
      requestKey: 'active',
      responseKey: 'Active',
      valueType: 'boolean',
      orderByKey: 'active',
    },
    Name: {
      cellJSX: EllipsisWithTooltipRender,
      cellRenderType: 'name_link',
      defaultOrderDirection: 'asc',
      headerRenderType: 'name',
      isSearchable: true,
      key: 'Name',
      label: 'Deal Name',
      tKey: 'DealName',
      requestKey: 'name',
      responseKey: 'Name',
      valueType: 'string',
      orderByKey: 'name',
    },
    ExternalDealID: {
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'ExternalDealID',
      label: 'External Deal ID',
      tKey: 'ExternalDealID',
      orderByKey: 'external_deal_id',
      requestKey: 'external_deal_id',
      responseKey: 'ExternalDealID',
      valueType: 'string',
    },
    StartDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'StartDate',
      label: 'Start Date',
      tKey: 'StartDate',
      orderByKey: 'start_date',
      requestKey: '',
      responseKey: 'StartDate',
      valueType: 'date',
    },
    EndDate: {
      cellJSX: DateTimeRenderUnchanged,
      cellRenderType: 'date',
      defaultOrderDirection: 'desc',
      headerRenderType: 'date',
      isDate: true,
      key: 'EndDate',
      label: 'End Date',
      tKey: 'EndDate',
      orderByKey: 'end_date',
      requestKey: '',
      responseKey: 'EndDate',
      valueType: 'date',
    },
    Partner: {
      cellJSX: DealPartnerRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Partner',
      label: 'Partner',
      tKey: 'Partner',
      orderByKey: 'partner',
      requestKey: '',
      responseKey: 'Partner',
      valueType: 'string',
    },
    Salesperson: {
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'Salesperson',
      label: 'Salesperson',
      tKey: 'Salesperson',
      orderByKey: 'salesperson_id',
      requestKey: '',
      responseKey: 'Salesperson',
      valueType: 'string',
    },
    Channels: {
      cellJSX: DealChannelsListModalLink,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number_small',
      key: 'Channels',
      label: 'Channels',
      tKey: 'Channels',
      orderByKey: 'targeting_channels',
      requestKey: '',
      responseKey: 'TargetingChannels',
      valueType: 'number',
    },
    ChannelGroups: {
      cellJSX: DealChannelGroupsListModalLink,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number_small',
      key: 'ChannelGroups',
      label: 'Channel Groups',
      tKey: 'ChannelGroups',
      orderByKey: 'targeting_channel_groups',
      requestKey: '',
      responseKey: 'TargetingChannelGroups',
      valueType: 'number',
    },
    DealType: {
      cellJSX: DealTypeRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'DealType',
      label: 'Deal Type',
      tKey: 'DealType',
      requestKey: 'deal_type',
      responseKey: 'DealType',
      valueType: 'number',
      orderByKey: 'deal_type',
    },
    DealTier: {
      cellJSX: DealTierRender,
      cellRenderType: 'string',
      defaultOrderDirection: 'asc',
      headerRenderType: 'string',
      key: 'DealTier',
      label: 'Deal Tier',
      tKey: 'DealTier',
      orderByKey: 'deal_tier',
      requestKey: 'deal_tier',
      responseKey: 'DealTier',
      valueType: 'number',
    },
    Impressions: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Impressions',
      label: 'Delivered Impressions',
      tKey: 'DeliveredImpressions',
      orderByKey: 'delivered_imps',
      requestKey: '',
      responseKey: 'DeliveredImps',
      valueType: 'number',
    },
    Goal: {
      cellRenderType: 'large_number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Goal',
      label: 'Goal',
      tKey: 'Goal',
      orderByKey: 'goal',
      requestKey: '',
      responseKey: 'Goal',
      valueType: 'number',
    },
    Actions: {
      cellRenderType: 'actions',
      disableSort: true,
      headerRenderType: 'actions',
      key: 'Actions',
      label: 'Actions',
      tKey: 'Actions',
      requestKey: '',
      responseKey: '',
      valueType: 'component',
    },
    Revenue: {
      cellJSX: MultiCurrencyRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'Revenue',
      label: 'Revenue',
      tKey: 'Revenue',
      requestKey: '',
      responseKey: 'Revenue',
      orderByKey: 'revenue',
      valueType: 'number',
    },
    CPM: {
      cellJSX: MultiCurrencyRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'CPM',
      label: 'Net CPM',
      tKey: 'NetCPM',
      orderByKey: 'cpm',
      requestKey: '',
      responseKey: 'CPM',
      valueType: 'number',
    },
    BidFloor: {
      cellJSX: MultiCurrencyRender,
      cellRenderType: 'number',
      defaultOrderDirection: 'desc',
      headerRenderType: 'number',
      key: 'BidFloor',
      label: 'Bid Floor',
      tKey: 'BidFloor',
      requestKey: '',
      responseKey: 'BidFloor',
      orderByKey: 'bid_floor',
      valueType: 'number',
    },
    CurrencySymbol: {
      key: 'CurrencySymbol',
      label: 'Currency Symbol',
      requestKey: '',
      responseKey: 'CurrencySymbol',
      valueType: 'string',
      isHidden: true,
    },
  },
};

export const formatDealsResponseIntoLVTRows = (
  deals: ISimpleDeal[],
  cfg: IListViewTableColumns,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  deals.forEach((deal) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: unknown = deal[responseKey as keyof ISimpleDeal];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};
